/**
 * Persistence for Monty's proactive hints, split across two stores on
 * purpose:
 *
 * - The opt-out lives in `localStorage` and is deliberately global -- not
 *   scoped to tenant or user. Someone who turns hints off wants them off,
 *   not off in one workspace. It survives reloads but not logout, which
 *   clears all of `localStorage`: because the key is not user-scoped,
 *   carrying it across a logout would hand the next person on a shared
 *   machine an opt-out they never chose.
 * - "Already hinted this session" lives in `sessionStorage`, keyed by user,
 *   so hints come back on the next visit but never twice in one sitting.
 *   The user key matters because switching accounts in a tab should not
 *   inherit the previous user's state.
 *
 * Kept free of React imports so it can be unit tested in isolation.
 */
import { logger } from './logger';

export const CHAT_HINTS_DISABLED_KEY = 'mn:chat:hints-disabled';

/**
 * Which message in a context's rotation to use next. Deliberately durable
 * rather than per-session: the session budget caps hints at two, so a
 * rotation keyed on the session counter could never reach a third message
 * and that copy would be dead. Carrying the index across sessions is what
 * makes every line reachable.
 */
export const CHAT_HINT_ROTATION_KEY = 'mn:chat:hint-rotation';

export interface ChatHintSessionState {
  /** Times a hint has been shown this session. */
  shownCount: number;
  /** True once the user closed a hint by hand, which ends the session. */
  dismissed: boolean;
}

export const EMPTY_CHAT_HINT_SESSION: ChatHintSessionState = {
  shownCount: 0,
  dismissed: false,
};

/** Returns 0 when unreadable or never set -- always a valid rotation index. */
export function readChatHintRotation(): number {
  try {
    const parsed = Number(localStorage.getItem(CHAT_HINT_ROTATION_KEY));
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
  } catch (err) {
    logger.warn('chatHintStorage: failed to read rotation index', err);
    return 0;
  }
}

export function advanceChatHintRotation(current: number): void {
  try {
    localStorage.setItem(CHAT_HINT_ROTATION_KEY, String(current + 1));
  } catch (err) {
    logger.warn('chatHintStorage: failed to persist rotation index', err);
  }
}

export function getChatHintSessionKey(userId: string): string {
  return `mn:chat:${userId}:hint-session`;
}

/**
 * Defaults to enabled. An unreadable store means we cannot prove the user
 * opted out, and hints are recoverable (one click to dismiss) where a
 * wrongly silenced feature is invisible.
 */
export function areChatHintsEnabled(): boolean {
  try {
    return localStorage.getItem(CHAT_HINTS_DISABLED_KEY) === null;
  } catch (err) {
    logger.warn('chatHintStorage: failed to read hint preference', err);
    return true;
  }
}

export function setChatHintsEnabled(enabled: boolean): void {
  try {
    if (enabled) {
      localStorage.removeItem(CHAT_HINTS_DISABLED_KEY);
    } else {
      localStorage.setItem(CHAT_HINTS_DISABLED_KEY, new Date().toISOString());
    }
  } catch (err) {
    logger.warn('chatHintStorage: failed to persist hint preference', err);
  }
}

/**
 * Unlike the preference above, an unreadable session store reports the
 * budget as spent: a session counter we cannot increment would let the
 * hint fire on every navigation.
 */
export function readChatHintSession(userId: string): ChatHintSessionState {
  try {
    const raw = sessionStorage.getItem(getChatHintSessionKey(userId));
    if (!raw) {
      return EMPTY_CHAT_HINT_SESSION;
    }
    const parsed = JSON.parse(raw);
    return {
      shownCount:
        typeof parsed?.shownCount === 'number' ? parsed.shownCount : 0,
      dismissed: parsed?.dismissed === true,
    };
  } catch (err) {
    logger.warn('chatHintStorage: failed to read session state', err);
    return { shownCount: Number.MAX_SAFE_INTEGER, dismissed: true };
  }
}

export function writeChatHintSession(
  userId: string,
  state: ChatHintSessionState,
): void {
  try {
    sessionStorage.setItem(
      getChatHintSessionKey(userId),
      JSON.stringify(state),
    );
  } catch (err) {
    logger.warn('chatHintStorage: failed to persist session state', err);
  }
}
